import { useSyncExternalStore } from "react";
import {
	type AssessContext,
	assessPlan,
	operationErrorOf,
	type PlanAction,
	type PlanItem,
	type PlanItemError,
	type PlanNames,
	planActionDevices,
	planActionTouchesDevices,
	startErrorOf,
} from "./printer-plan";
import type { OperationStarted, PharmacyPrintersResponse, PrinterOperation } from "./printers-api";
import { observeOperation } from "./step-timeline";

/**
 * Szkic planu jednego miejsca (apteka/grupa) jednego użytkownika: pozycje w
 * localStorage (przeżywają przeładowanie), stan wykonania w pamięci karty.
 * Wykonanie trwa, dopóki karta jest otwarta — także po przejściu do innej
 * strony aplikacji, bo magazyn żyje poza komponentem.
 */
export type PlanRun = {
	/** Ta karta wykonuje teraz plan. */
	active: boolean;
	/** „Zatrzymaj po bieżącej” — plan kończy po trwającej pozycji. */
	stopRequested: boolean;
	currentId: string | null;
};

export type PlanSnapshot = { items: PlanItem[]; run: PlanRun };

const STORAGE_PREFIX = "farmaster_drukarki_plan:";
const IDLE: PlanRun = { active: false, stopRequested: false, currentId: null };

/** Tyle kolejnych błędów odczytu stanu operacji (sieć) toleruje wykonanie, zanim się zatrzyma. */
const MAX_POLL_ERRORS = 5;

const ITEM_STATUSES = new Set(["draft", "running", "succeeded", "failed"]);

/** Pozycja z localStorage ma kształt, którego panel oczekuje (zapis mógł się uszkodzić). */
function isPlanItem(value: unknown): value is PlanItem {
	if (typeof value !== "object" || value === null) return false;
	const item = value as Partial<PlanItem>;
	return (
		typeof item.id === "string" &&
		typeof item.status === "string" &&
		ITEM_STATUSES.has(item.status) &&
		typeof item.action === "object" &&
		item.action !== null &&
		typeof item.action.kind === "string" &&
		typeof item.names === "object" &&
		item.names !== null
	);
}

function readItems(key: string): PlanItem[] {
	try {
		const raw = globalThis.localStorage?.getItem(STORAGE_PREFIX + key);
		if (!raw) return [];
		const parsed = JSON.parse(raw) as { version?: number; items?: unknown };
		return parsed?.version === 1 && Array.isArray(parsed.items)
			? parsed.items.filter(isPlanItem)
			: [];
	} catch {
		return [];
	}
}

function writeItems(key: string, items: PlanItem[]) {
	try {
		if (items.length === 0) globalThis.localStorage?.removeItem(STORAGE_PREFIX + key);
		else
			globalThis.localStorage?.setItem(STORAGE_PREFIX + key, JSON.stringify({ version: 1, items }));
	} catch {
		// Pełny localStorage — plan działa dalej w pamięci karty.
	}
}

let idCounter = 0;
function newId(): string {
	idCounter += 1;
	return `${Date.now().toString(36)}-${idCounter.toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export type RunnerDeps = {
	start: (action: PlanAction) => Promise<OperationStarted>;
	getOperation: (operationId: number) => Promise<PrinterOperation>;
	/** Świeży widok miejsca (i sieci komputerów) do oceny pozycji tuż przed wysyłką. */
	getContext: () => Promise<Omit<AssessContext, "earlier">>;
	onOperationFinished?: (operation: PrinterOperation) => void;
	sleep?: (ms: number) => Promise<void>;
	/** Odpytywanie stanu operacji (kontrakt: co 2 s). */
	pollMs?: number;
	/** Najdłużej tyle czekamy na świeży inwentarz komputerów po operacji. */
	inventorySettleMs?: number;
};

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export class PlanStore {
	readonly key: string;
	private snapshot: PlanSnapshot;
	private listeners = new Set<() => void>();
	private watching = new Set<number>();

	constructor(key: string) {
		this.key = key;
		this.snapshot = { items: readItems(key), run: IDLE };
	}

	getSnapshot = (): PlanSnapshot => this.snapshot;

	subscribe = (listener: () => void): (() => void) => {
		this.listeners.add(listener);
		return () => this.listeners.delete(listener);
	};

	private set(items: PlanItem[], run: PlanRun = this.snapshot.run) {
		const itemsChanged = items !== this.snapshot.items;
		this.snapshot = { items, run };
		if (itemsChanged) writeItems(this.key, items);
		for (const listener of this.listeners) listener();
	}

	private setRun(patch: Partial<PlanRun>) {
		this.set(this.snapshot.items, { ...this.snapshot.run, ...patch });
	}

	private patchItem(id: string, patch: Partial<PlanItem>) {
		this.set(this.snapshot.items.map((item) => (item.id === id ? { ...item, ...patch } : item)));
	}

	add(action: PlanAction, names: PlanNames): PlanItem {
		const item: PlanItem = {
			id: newId(),
			action,
			names,
			addedAt: new Date().toISOString(),
			status: "draft",
			operationId: null,
			error: null,
			startedAt: null,
			finishedAt: null,
		};
		this.set([...this.snapshot.items, item]);
		return item;
	}

	/** Usunięcie pozycji — poza tą, która właśnie się wykonuje. */
	remove(id: string) {
		const item = this.snapshot.items.find((i) => i.id === id);
		if (!item || item.status === "running") return;
		this.set(this.snapshot.items.filter((i) => i.id !== id));
	}

	/** Zmiana parametrów szkicu (np. „ustaw jako domyślną” przy udostępnieniu). */
	updateAction(id: string, action: PlanAction) {
		const item = this.snapshot.items.find((i) => i.id === id);
		if (item?.status !== "draft") return;
		this.patchItem(id, { action });
	}

	/** Przesuń pozycję szkicu o jedno miejsce (kolejność = kolejność wykonania). */
	move(id: string, delta: -1 | 1) {
		const items = [...this.snapshot.items];
		const index = items.findIndex((i) => i.id === id);
		const target = index + delta;
		if (index < 0 || target < 0 || target >= items.length) return;
		if (items[index].status === "running" || items[target].status === "running") return;
		[items[index], items[target]] = [items[target], items[index]];
		this.set(items);
	}

	/** Wyczyść plan: wszystko poza trwającą pozycją (tę widać do końca operacji). */
	clear() {
		this.set(this.snapshot.items.filter((i) => i.status === "running"));
	}

	/** Usuń wykonane pozycje — plan wraca do tego, co zostało. */
	clearSucceeded() {
		this.set(this.snapshot.items.filter((i) => i.status !== "succeeded"));
	}

	requestStop() {
		if (this.snapshot.run.active) this.setRun({ stopRequested: true });
	}

	private fail(id: string, error: PlanItemError) {
		this.patchItem(id, { status: "failed", error, finishedAt: new Date().toISOString() });
	}

	/** Odpytuje operację do stanu końcowego; `null` = nie udało się odczytać stanu. */
	private async pollOperation(
		operationId: number,
		deps: RunnerDeps,
	): Promise<PrinterOperation | null> {
		const sleep = deps.sleep ?? defaultSleep;
		let errors = 0;
		for (;;) {
			try {
				const operation = await deps.getOperation(operationId);
				errors = 0;
				observeOperation(operation);
				if (operation.status !== "running") return operation;
			} catch {
				errors += 1;
				if (errors >= MAX_POLL_ERRORS) return null;
			}
			await sleep(deps.pollMs ?? 2_000);
		}
	}

	/**
	 * Po operacji agent wysyła świeży inwentarz już PO acku — następna pozycja
	 * oceniana na starym widoku zobaczyłaby nieaktualny stan (backend tak samo).
	 * Czekamy, aż komputery online z tej pozycji zgłoszą inwentarz nowszy niż
	 * koniec operacji (najdłużej `inventorySettleMs`).
	 */
	private async waitForInventory(
		item: PlanItem,
		operation: PrinterOperation,
		view: PharmacyPrintersResponse,
		deps: RunnerDeps,
	) {
		if (!planActionTouchesDevices(item.action) || !operation.finishedAt) return;
		const devices = planActionDevices(item.action, view);
		const finishedAt = new Date(operation.finishedAt).getTime();
		const sleep = deps.sleep ?? defaultSleep;
		const deadline = Date.now() + (deps.inventorySettleMs ?? 12_000);
		let current = view;
		while (Date.now() < deadline) {
			const fresh = devices.every((uuid) => {
				const device = current.devices.find((d) => d.deviceUuid === uuid);
				if (!device?.online) return true;
				return device.inventoryAt != null && new Date(device.inventoryAt).getTime() >= finishedAt;
			});
			if (fresh) return;
			await sleep(deps.pollMs ?? 2_000);
			try {
				current = (await deps.getContext()).view;
			} catch {
				return;
			}
		}
	}

	/**
	 * Wykonaj plan po kolei: tylko pozycje zatwierdzone przez operatora
	 * (`approved` — to, co widział i potwierdził), bez niewykonalnych w chwili
	 * startu. Każda kolejna czeka na stan końcowy poprzedniej, a pierwszy błąd
	 * (odmowa startu, 409, porażka operacji, pozycja niewykonalna tuż przed
	 * wysyłką) zatrzymuje plan. Nieudana pozycja zostaje w planie — „Wznów”
	 * spróbuje jej ponownie, a można ją też usunąć.
	 */
	async run(deps: RunnerDeps, approved?: ReadonlySet<string>): Promise<void> {
		if (this.snapshot.run.active) return;
		if (this.snapshot.items.some((i) => i.status === "running")) return;
		this.setRun({ active: true, stopRequested: false, currentId: null });
		try {
			let ctx0: Omit<AssessContext, "earlier">;
			try {
				ctx0 = await deps.getContext();
			} catch (error) {
				// Widok miejsca nie dał się pobrać — nic nie wystartowało; błąd przy pierwszej pozycji.
				const first = this.snapshot.items.find(
					(i) =>
						(i.status === "draft" || i.status === "failed") && (!approved || approved.has(i.id)),
				);
				if (first) this.fail(first.id, startErrorOf(error));
				return;
			}
			const initial = assessPlan(this.snapshot.items, ctx0);
			const queue = this.snapshot.items
				.filter(
					(i) =>
						(i.status === "draft" || i.status === "failed") &&
						(!approved || approved.has(i.id)) &&
						initial.get(i.id)?.state !== "blocked",
				)
				.map((i) => i.id);

			for (let index = 0; index < queue.length; index += 1) {
				const id = queue[index];
				if (this.snapshot.run.stopRequested) break;
				if (!this.item(id)) continue; // usunięta w trakcie
				this.setRun({ currentId: id });
				const ctx = index === 0 ? ctx0 : await deps.getContext();
				if (this.snapshot.run.stopRequested) break;
				// Druga karta tego samego planu mogła już wysłać tę pozycję.
				const elsewhere = readItems(this.key).find((i) => i.id === id);
				if (elsewhere && (elsewhere.status === "running" || elsewhere.status === "succeeded")) {
					this.set(readItems(this.key));
					break;
				}
				const item = this.item(id);
				if (!item) continue;
				const resolved = await this.resolveUnknown(item, deps);
				if (resolved === "succeeded") continue;
				if (resolved === "unknown") break;
				const assessment = assessPlan(this.snapshot.items, ctx).get(id);
				if (assessment?.state === "blocked") {
					this.fail(id, { title: "Nie da się wykonać", hint: assessment.reason });
					break;
				}
				this.patchItem(id, {
					status: "running",
					error: null,
					operationId: null,
					outcomeUnknown: false,
					startedAt: new Date().toISOString(),
					finishedAt: null,
				});
				let started: OperationStarted;
				try {
					started = await deps.start(item.action);
				} catch (error) {
					this.fail(id, startErrorOf(error));
					break;
				}
				this.patchItem(id, { operationId: started.operationId });
				const operation = await this.pollOperation(started.operationId, deps);
				if (!operation) {
					this.failUnknown(id);
					break;
				}
				deps.onOperationFinished?.(operation);
				if (operation.status === "failed") {
					this.fail(id, operationErrorOf(operation.cause, operation.errorDetail, operation.kind));
					break;
				}
				this.patchItem(id, {
					status: "succeeded",
					finishedAt: operation.finishedAt ?? new Date().toISOString(),
				});
				if (index < queue.length - 1 && !this.snapshot.run.stopRequested) {
					await this.waitForInventory(item, operation, ctx.view, deps);
				}
			}
		} catch (error) {
			// Widok miejsca nie dał się pobrać — nic nie wystartowało w tym kroku.
			const current = this.snapshot.run.currentId;
			if (current && this.item(current)?.status !== "running")
				this.fail(current, startErrorOf(error));
		} finally {
			this.setRun({ active: false, stopRequested: false, currentId: null });
		}
	}

	private item(id: string): PlanItem | undefined {
		return this.snapshot.items.find((i) => i.id === id);
	}

	private failUnknown(id: string) {
		this.patchItem(id, {
			status: "failed",
			outcomeUnknown: true,
			error: {
				title: "Nie udało się odczytać stanu operacji",
				hint: "Operacja mogła się wykonać. „Wznów” najpierw sprawdzi jej stan, zanim wyśle ją ponownie.",
			},
			finishedAt: new Date().toISOString(),
		});
	}

	/**
	 * Pozycja z operacją o nieznanym wyniku: odczytaj tę operację, zamiast
	 * wysyłać drugą. `retry` — zakończyła się porażką, można wysłać ponownie;
	 * `succeeded` — wykonała się; `unknown` — nadal nie wiadomo (plan staje).
	 */
	private async resolveUnknown(
		item: PlanItem,
		deps: RunnerDeps,
	): Promise<"retry" | "succeeded" | "unknown"> {
		if (!item.outcomeUnknown || item.operationId == null) return "retry";
		this.patchItem(item.id, { status: "running", error: null });
		const operation = await this.pollOperation(item.operationId, deps);
		if (!operation) {
			this.failUnknown(item.id);
			return "unknown";
		}
		deps.onOperationFinished?.(operation);
		if (operation.status === "failed") {
			this.patchItem(item.id, { status: "failed", outcomeUnknown: false });
			return "retry";
		}
		this.patchItem(item.id, {
			status: "succeeded",
			outcomeUnknown: false,
			finishedAt: operation.finishedAt ?? new Date().toISOString(),
		});
		return "succeeded";
	}

	/** Zapis z innej karty (zdarzenie `storage`) — przejmij, gdy ta karta nic nie wykonuje. */
	reloadFromStorage() {
		if (this.snapshot.run.active || this.watching.size > 0) return;
		this.snapshot = { items: readItems(this.key), run: this.snapshot.run };
		for (const listener of this.listeners) listener();
	}

	/**
	 * Po przeładowaniu strony pozycja `running` z operacją jest obserwowana do
	 * końca; plan NIE rusza dalej sam — operator widzi wynik i wznawia.
	 */
	async watchOrphans(deps: RunnerDeps): Promise<void> {
		if (this.snapshot.run.active) return;
		for (const item of this.snapshot.items) {
			if (item.status !== "running") continue;
			if (item.operationId == null) {
				// Karta zamknięta między kliknięciem a odpowiedzią 202 — nie wiemy, czy ruszyła.
				this.fail(item.id, {
					title: "Wysyłka przerwana",
					hint: "Karta została zamknięta podczas wysyłania — sprawdź Historię, a potem wznów plan.",
				});
				continue;
			}
			if (this.watching.has(item.operationId)) continue;
			this.watching.add(item.operationId);
			const operation = await this.pollOperation(item.operationId, deps);
			this.watching.delete(item.operationId);
			if (!operation) {
				this.failUnknown(item.id);
				continue;
			}
			deps.onOperationFinished?.(operation);
			if (operation.status === "failed") {
				this.fail(
					item.id,
					operationErrorOf(operation.cause, operation.errorDetail, operation.kind),
				);
			} else {
				this.patchItem(item.id, {
					status: "succeeded",
					finishedAt: operation.finishedAt ?? new Date().toISOString(),
				});
			}
		}
	}
}

const stores = new Map<string, PlanStore>();

// Ten sam plan otwarty w dwóch kartach: zapis jednej odświeża drugą.
if (typeof window !== "undefined") {
	window.addEventListener("storage", (event) => {
		if (!event.key?.startsWith(STORAGE_PREFIX)) return;
		stores.get(event.key.slice(STORAGE_PREFIX.length))?.reloadFromStorage();
	});
}

/** Magazyn planu dla klucza `tenant:użytkownik:miejsce` (jeden na kartę). */
export function getPlanStore(key: string): PlanStore {
	let store = stores.get(key);
	if (!store) {
		store = new PlanStore(key);
		stores.set(key, store);
	}
	return store;
}

/** Tylko do testów: zapomnij magazyny (localStorage czyści test). */
export function resetPlanStoresForTests(): void {
	stores.clear();
}

export function usePlanSnapshot(store: PlanStore): PlanSnapshot {
	return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}
